import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, Download, RefreshCw } from "lucide-react";
import { useState } from "react";
import {
  getShowTimeEntryMirror,
  getTimeEntryMirrorEmployeeReportDownloadUrl,
} from "@/Api/timeEntryMirror";
import { cn } from "@/lib/utils";

const STATUS_COLORS: Record<number, string> = {
  1: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  2: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  3: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  4: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  5: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const STATUS_LABELS: Record<number, string> = {
  1: "Pendente",
  2: "Processando",
  3: "Concluído",
  4: "Falhou",
  5: "Falhou",
};

const SIGNATURE_STATUS_COLORS: Record<number, string> = {
  1: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  2: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
};

const SIGNATURE_STATUS_LABELS: Record<number, string> = {
  1: "Assinatura pendente",
  2: "Assinado",
};

export const ShowBtn = ({ uuid }: { uuid: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [request, setRequest] = useState<any>(null);

  const fetchData = async () => {
    setLoading(true);
    const { data } = await getShowTimeEntryMirror(uuid);
    setRequest(data?.response);
    setLoading(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) fetchData();
  };

  const handleDownload = async (employeeReportUuid: string, employeeReportId: number) => {
    setDownloading(employeeReportId);
    const { data, ok } = await getTimeEntryMirrorEmployeeReportDownloadUrl(uuid, employeeReportUuid);
    setDownloading(null);
    if (!ok || !data?.response?.url) return;
    window.open(data.response.url, "_blank");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="size-7 p-0 hover:bg-stone-950/10" title="Ver detalhes">
          <Eye className="size-5 text-blue-600" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogDescription className="text-lg">Espelho de Ponto</DialogDescription>
          <DialogTitle className="text-2xl">Colaboradores do Relatório</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
            <RefreshCw className="size-4 animate-spin" /> Carregando...
          </div>
        )}

        {!loading && request && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-muted-foreground">
              Período: {new Date(request.date_start).toLocaleDateString("pt-BR", { timeZone: "UTC" })} a{" "}
              {new Date(request.date_end).toLocaleDateString("pt-BR", { timeZone: "UTC" })}
            </p>
            {request.employee_reports?.length < 1 && (
              <p className="text-sm text-muted-foreground py-4 text-center">
                Nenhum colaborador processado ainda.
              </p>
            )}
            {request.employee_reports?.map((report: any) => (
              <div
                key={report.id}
                className="flex items-center justify-between gap-2 border rounded p-3"
              >
                <div className="flex flex-col">
                  <span className="font-medium">{report.employee?.user?.name}</span>
                  {report.status === 5 && report.error_message && (
                    <span className="text-xs text-red-500">{report.error_message}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "px-2 py-1 rounded-full text-xs font-medium",
                      STATUS_COLORS[report.status]
                    )}
                  >
                    {STATUS_LABELS[report.status]}
                  </span>
                  {report.status === 3 && report.signature_status && (
                    <span
                      className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium",
                        SIGNATURE_STATUS_COLORS[report.signature_status]
                      )}
                      title={
                        report.signed_at
                          ? `Assinado em ${new Date(report.signed_at).toLocaleString("pt-BR")}`
                          : undefined
                      }
                    >
                      {SIGNATURE_STATUS_LABELS[report.signature_status]}
                    </span>
                  )}
                  {report.status === 3 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 p-0 hover:bg-stone-950/10"
                      title="Baixar PDF"
                      disabled={downloading === report.id}
                      onClick={() => handleDownload(report.uuid, report.id)}
                    >
                      <Download className="size-5 text-blue-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
